<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useScheduleStore } from '@/stores/schedule.store'
import { useClone, useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useNotify } from '@/composables/notify'
import { useTranslations } from '@/composables/translations'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import ExtraFieldsEdit from '@/components/shared/extra-fields/ExtraFieldsEdit.vue'
import IconSelect from '@/components/shared/IconSelect.vue'
import ColorInput from '@/components/shared/ColorInput.vue'
import TaskTypeChip from '@/components/task/TaskTypeChip.vue'

const ICONS = Object.freeze([
  'mdi-movie-open-outline',
  'mdi-camera-outline',
  'mdi-video-outline',
  'mdi-microphone-outline',
  'mdi-music-note-outline',
  'mdi-lightbulb-on-outline',
  'mdi-map-marker-outline',
  'mdi-truck-outline',
  'mdi-account-group-outline',
  'mdi-file-document-outline',
  'mdi-palette-outline',
  'mdi-scissors-cutting',
  'mdi-clipboard-check-outline',
  'mdi-flag-outline',
  'mdi-star-outline',
  'mdi-alert-outline',
])
const DEFAULT_COLOR = '#1976d2'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const { withTranslatedFields } = useTranslations()
const scheduleStore = useScheduleStore()

const form = ref(null)
const editItem = ref({ name: '', icon: ICONS[0], color: DEFAULT_COLOR, extra_fields: [] })
const showDeleteDialog = ref(false)
const { isUpdated, isUpdating, setOriginal } = useIsUpdated(editItem)

const isNew = computed(() => !editItem.value.id)

useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))

onMounted(() => {
  editItem.value = { ...editItem.value, ...useClone(props.modelValue) }
  setOriginal(editItem.value)
})

const onSave = async() => {
  if (!await form.value.validate()) return
  isUpdating.value = true
  try {
    const { id, name, icon, color, translations, extra_fields: extraFields } = editItem.value
    const { data } = await scheduleStore.upsertTaskTypeAsync({
      id,
      icon,
      color,
      translations: withTranslatedFields({ name: name.trim() }, translations),
      extra_fields: extraFields.map(field => ({
        id: field.id,
        field_type: field.field_type,
        options: field.options,
        translations: withTranslatedFields({ name: field.name.trim() }, field.translations),
      })),
    })
    await scheduleStore.fetchTaskTypesAsync()
    notifySuccess(i18n.t(isNew.value ? 'info.createdSuccessfully' : 'info.updatedSuccessfully'))
    emit('update', data)
  } finally {
    isUpdating.value = false
  }
}

const onDelete = async() => {
  await scheduleStore.deleteTaskTypeAsync(editItem.value.id)
  await scheduleStore.fetchTaskTypesAsync()
  notifySuccess(i18n.t('info.deletedSuccessfully'))
  emit('update')
}
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete"
    />
    <navigate-back :text="isNew ? i18n.t('common.tasks.newTaskType') : editItem.name" @cancel="emit('cancel')" />
    <q-card-section class="scroll col q-pa-md" :class="{ 'dialog-card-content': !isNew }">
      <q-form ref="form" @submit.prevent="onSave">
        <div class="row q-col-gutter-sm">
          <q-input
            v-model="editItem.name"
            :class="isMobile ? 'col-12' : 'col-6'"
            :label="i18n.t('common.name')"
            autofocus
            lazy-rules
            :rules="[val => !!val?.trim() || i18n.t('common.required')]"
          >
            <template #append>
              <task-type-chip :task-type="editItem" />
            </template>
          </q-input>
          <icon-select v-model="editItem.icon" :class="isMobile ? 'col-6' : 'col-3'" :options="ICONS" />
          <color-input v-model="editItem.color" :class="isMobile ? 'col-6' : 'col-3'" />
          <form-section-title :label="i18n.t('common.extraFields.title')" />
          <extra-fields-edit v-model="editItem.extra_fields" class="col-12" />
        </div>
      </q-form>
    </q-card-section>
    <q-card-actions v-if="isNew" align="right" class="q-pa-md">
      <q-btn
        color="primary"
        :label="i18n.t('common.confirm')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
    <q-card-actions v-else class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDeleteDialog = true"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
  </q-card>
</template>
